// src/data/treinamentosData.js
/**
 * Base de Dados Estruturada de Treinamentos e POPs - Sempre Joias
 * Organizada em 2 Grandes Trilhas:
 * 1. Atendimento, Comercial & Expedição (4 Etapas)
 * 2. Fábrica, Oficina & Equipamentos (7 Etapas)
 */

export const TREINAMENTOS_DATA = {
  atendimento: {
    id: "atendimento",
    title: "Atendimento, Comercial & Expedição",
    shortTitle: "Atendimento & Vendas",
    icon: "Headphones",
    color: "#3B82F6",
    badgeColor: "bg-blue-100 text-blue-800 border-blue-200",
    description: "Da primeira mensagem no WhatsApp/Instagram ao unboxing impecável: técnicas de venda, orçamentos, protocolos de SAC, política de garantia de 60 dias, logística reversa e embalagem.",
    etapas: [
      {
        id: "atend-etapa-1",
        number: "01",
        title: "Vendas Consultivas & Atendimento Digital",
        description: "Abordagem no WhatsApp e Instagram, quebra de objeções, anéis de formatura e pedras naturais vs zircônias.",
        aulas: [
          {
            id: "atend-1-1",
            code: "AULA 1.1",
            title: "Abordagem Inicial e Sondagem no WhatsApp/Instagram",
            estimated_minutes: 15,
            youtube_id: "", // Pronto para receber ID do YouTube sob demanda
            key_points: [
              "Tom de voz acolhedor: sempre use 'Bom dia/Boa tarde! Tudo bem e com você?' antes de responder o produto.",
              "Identifique o canal de origem (Instagram Direct ou link direto do site).",
              "Descubra se a formatura ou evento tem data próxima ou distante.",
              "Para clientes desconfiados: apresente o CNPJ (17.511.657/0001-16) e o link oficial de avaliações do Google."
            ],
            content: `
### 1. Padrão de Recepção e Boas-Vindas
O primeiro contato define a percepção de valor da Sempre Joias. O cliente não está apenas comprando uma joia, mas celebrando uma conquista importante (formatura, noivado, casamento).

**Regra de Ouro:** Nunca responda seco com apenas o preço. Sempre humanize a conversa.

\`\`\`text
Exemplo de entrada: "Boa tarde! Tudo bem? Tem anel de formatura de biologia com a pedra água marinha?"
Resposta Padrão: "Boa tarde! Tudo bem e com você? Temos sim! A água-marinha é a pedra natural oficial do curso de biologia. Vou te enviar fotos reais do nosso modelo para você conhecer os detalhes."
\`\`\`

---

### 2. Como Lidar com Clientes Desconfiados
Muitos clientes já tiveram más experiências em compras online de joias. Quando perceber hesitação ou receio de fraude:
1. **Transparência imediata:** Envie os dados oficiais da empresa sem que ele precise pedir.
   - CNPJ: \`17.511.657/0001-16\`
   - Link de avaliações do Google: \`https://g.co/kgs/jankyG5\`
2. **Convite para o Showroom Físico:** Se o cliente for de São Paulo ou região metropolitana, ofereça agendamento presencial no showroom:
   - "Você pode vir presencialmente tomar um café conosco em nosso showroom em SP e provar os modelos!"
            `,
            pop: null,
            checklist: [
              "Saudou o cliente pelo nome e com tom amigável?",
              "Identificou o curso/modelo de interesse?",
              "Enviou fotos de alta qualidade do modelo no drive/catálogo?",
              "Se o cliente demonstrou insegurança, enviou o CNPJ e o link do Google Avaliações?"
            ]
          },
          {
            id: "atend-1-2",
            code: "AULA 1.2",
            title: "Venda Consultiva: Formatura, Símbolos & Pedras Naturais",
            estimated_minutes: 20,
            youtube_id: "",
            key_points: [
              "Conheça a anatomia do modelo Vega, Atlas, Triunfo e Solitários.",
              "Explique com autoridade o significado dos símbolos de cada curso (ex: ramos de louro = vitória).",
              "Diferencie claramente a pedra natural da zircônia sintética.",
              "Apresente a cotação com transparência: valor da joia base + opcional de pedra natural."
            ],
            content: `
### 1. A Narrativa dos Símbolos
Em anéis de formatura, o cliente quer saber o significado de cada emblema esculpido na joia. Conhecer essa história gera conexão imediata.
- **Biologia (Exemplo Real):** "De um lado temos o ramo de louros, sinal de vitória e superação, abraçando o microscópio ou a serpente; do outro lado temos o símbolo representativo da ciência da vida."
- **Direito, Medicina, Engenharias:** Consulte a tabela de símbolos de cada curso no catálogo antes de responder dúvidas complexas.

---

### 2. Pedras Naturais vs Zircônias
- **Zircônia:** Pedra de laboratório com alto brilho, durabilidade e valor acessível (já inclusa no valor padrão do anel).
- **Pedra Natural (Gema):** Extraída da natureza, com certificado gemológico, exclusividade de cor e lapidação nobre (ex: Água-Marinha, Esmeralda, Rubi, Safira, Topázio).
- **Como orçar pedra natural:** Caso o cliente deseje pedra natural, consulte a cotação atualizada na tabela interna de gemas e passe o orçamento claro:
  - Exemplo: *"O anel em Prata 925 com zircônia sai por R$ 380,00. Caso queira com a Água-Marinha natural autêntica lapidada, o valor fica em R$ 996,00."*
            `,
            pop: {
              tag: "POP ATEND-01",
              title: "Cotação e Apresentação de Joia de Formatura com Pedra Natural",
              when_to_use: "Quando o cliente solicitar personalização com pedras naturais ou dúvidas de símbolos.",
              responsible: "Consultor de Atendimento / Vendas Online",
              tools: ["Tabela de Pedras Naturais", "Pasta do Google Drive com fotos reais", "Calculadora de Preço"],
              steps: [
                "Identifique o curso exato e se o cliente deseja anel masculino ou feminino.",
                "Consulte a pedra oficial correspondente na tabela da Sempre Joias.",
                "Envie 2 a 3 fotos nítidas do modelo com iluminação de estúdio.",
                "Explique o significado dos emblemas laterais com linguagem nobre.",
                "Apresente a comparação entre Zircônia e Pedra Natural com as respectivas cotações.",
                "Pergunte qual opção se encaixa melhor no estilo e planejamento do formando."
              ],
              critical_points: "Nunca garanta disponibilidade de pedra natural rara sem checar o estoque físico ou fornecedor.",
              expected_result: "Cliente esclarecido e seguro quanto à diferença de valor, pronto para definir a numeração."
            },
            checklist: [
              "Confirmou o curso correto e o modelo preferido?",
              "Explicou a diferença entre pedra natural e zircônia?",
              "Consultou a disponibilidade da pedra no estoque?",
              "Passou as opções de parcelamento e pagamento à vista com desconto?"
            ]
          },
          {
            id: "atend-1-3",
            code: "AULA 1.3",
            title: "Dúvidas de Numeração, Objeções e Fechamento",
            estimated_minutes: 15,
            youtube_id: "",
            key_points: [
              "Se o cliente estiver muito em dúvida sobre o aro e for de SP, convide para o showroom.",
              "Para clientes de fora de SP: envie o guia prático de medição ou recomende aneleira em joalheria física.",
              "Lembre que a Sempre Joias oferece ajuste gratuito de aro caso não sirva perfeitamente.",
              "Conduza para o fechamento com link direto no site ou pedido manual via ISET."
            ],
            content: `
### 1. Como Eliminar o Medo da Numeração Errada
A maior barreira de compra de anéis online é: *"E se não servir no meu dedo?"*.

**Roteiro de Segurança para o Cliente:**
1. "Fique tranquilo! Caso você receba o anel e o tamanho não fique 100% confortável, nós fazemos o primeiro ajuste de cortesia sem nenhum custo de mão de obra."
2. "Se for de São Paulo, você pode vir em nosso showroom tirar a medida exata na nossa aneleira de precisão."
3. "Se for de outro estado, nossa recomendação mais segura é passar em qualquer joalheria ou ótica da sua cidade e medir o aro na aneleira física."

---

### 2. Fechamento da Venda
Assim que o cliente aprovar o modelo e o aro:
- Solicite os dados completos (Nome completo, CPF, CEP e e-mail).
- Gere o link de checkout ou cadastre o pedido diretamente na ISET.
- Envie a confirmação com o resumo dos itens, prazo de fabricação e previsão de entrega.
            `,
            pop: null,
            checklist: [
              "Orientou o cliente sobre o método mais confiável de medição de aro?",
              "Ressaltou a garantia de ajuste de tamanho da Sempre Joias?",
              "Confirmou o endereço completo com CEP e dados para nota fiscal?",
              "Enviou o link de pagamento e explicou o prazo de produção?"
            ]
          }
        ]
      },
      {
        id: "atend-etapa-2",
        number: "02",
        title: "Protocolo de SAC & Política de Garantia",
        description: "Atendimento investigativo sem atrito, árvore de decisão dos 60 dias, reposição de pedras e custos de frete.",
        aulas: [
          {
            id: "atend-2-1",
            code: "AULA 2.1",
            title: "Investigação Preliminar e Escuta Ativa no SAC",
            estimated_minutes: 15,
            youtube_id: "",
            key_points: [
              "Entenda 100% do problema antes de propor qualquer solução.",
              "Pergunte ao cliente apenas o indispensável (e-mail, número do pedido, fotos e se ainda tem a pedra).",
              "NÃO pergunte dados que você mesmo pode consultar no sistema (data de entrega, histórico anterior).",
              "Mantenha sempre empatia: o cliente pode estar chateado por um presente que quebrou."
            ],
            content: `
### 1. Protocolo de Investigação
Quando um cliente entra em contato com reclamação ou solicitação de suporte, a regra de ouro é **descobrir o histórico nos sistemas antes de dar qualquer resposta definitiva**.

#### O QUE PERGUNTAR AO CLIENTE:
- E-mail cadastrado na compra ou número do pedido.
- Fotos e vídeos nítidos do estado atual da peça.
- Em caso de queda de pedra: **"Você ainda está com a pedrinha guardada?"** (Isso muda o custo e a rapidez da manutenção).

#### O QUE DESCOBRIR SOZINHO NOS SISTEMAS (NÃO PERGUNTE AO CLIENTE):
- **Quando o pedido foi entregue?** Verifique o rastreio na ISET/Correios.
- **Faz menos ou mais de 60 dias que o cliente recebeu?** O prazo de 60 dias é o marco legal da nossa garantia integral.
- **É um problema recorrente?** Verifique as notas do pedido na ISET para checar se já houve envio anterior de logística reversa ou ajuste prévio.
            `,
            pop: null,
            checklist: [
              "Localizou o pedido na ISET antes de prometer soluções?",
              "Checou a data exata da entrega no rastreio dos Correios?",
              "Verificou se o cliente ainda possui a pedra solta?",
              "Conferiu se há notas de atendimentos anteriores?"
            ]
          },
          {
            id: "atend-2-2",
            code: "AULA 2.2",
            title: "Árvore de Decisão de Garantia: Queda de Pedras e Reparos",
            estimated_minutes: 20,
            youtube_id: "",
            key_points: [
              "Queda de pedra < 60 dias (1ª vez): custo 100% nosso (logística reversa gratuita + conserto).",
              "Queda de pedra > 60 dias ou reincidente: taxa padrão de R$ 25,00 + frete por conta do cliente.",
              "Peça amassada, riscada por mau uso ou sem a pedra: cobrar reposição de materiais.",
              "Sempre anote na ISET exatamente o código da autorização de postagem gerada e a data."
            ],
            content: `
### Árvore de Decisão Operacional

#### CENÁRIO A: "A pedra do meu anel caiu"
- **Dados colhidos:** É a primeira ocorrência E faz menos de 60 dias que a peça foi entregue.
- **Procedimento:**
  1. Enviar código de logística reversa gratuita dos Correios (\`/li\` ou \`/reversa\`).
  2. Custo de envio e manutenção **100% por conta da Sempre Joias**.
  3. Registrar nas notas do pedido na ISET: \`Pedra caiu <60d. Enviada reversa AD1284587458BR - [Data] por [Seu Nome]\`.

#### CENÁRIO B: "A pedra caiu faz mais de 60 dias" ou "Perdi a pedra"
- **Procedimento:**
  1. Se tiver nota fiscal/histórico na ISET e o cliente não tiver a pedra: cobramos a taxa padrão de **R$ 25,00** para reposição da pedra padrão + custo de frete.
  2. Se a pedra for uma gema natural rara, é necessário consultar a oficina/estoque para orçamento da pedra avulsa.

#### CENÁRIO C: Peça danificada por impacto ou oxidação natural
- Joias em Prata 925 oxidam naturalmente com suor, ácido úrico ou perfumes.
- Explicar o uso da **Flanela Mágica** e produtos limpa-prata (Monzi) antes de recolher a joia desnecessariamente.
            `,
            pop: {
              tag: "POP SAC-01",
              title: "Abertura de Chamado de Garantia e Geração de Logística Reversa",
              when_to_use: "Quando o cliente relatar defeito de fabricação ou queda de pedra dentro do prazo de garantia.",
              responsible: "Atendente do SAC / Pós-Venda",
              tools: ["Sistema ISET", "Painel Correios / Melhor Envio", "WhatsApp de Atendimento"],
              steps: [
                "Confirme o recebimento das fotos/vídeos da peça com defeito.",
                "Valide na ISET se a entrega ocorreu há menos de 60 dias.",
                "Gere a autorização de postagem reversa (código numérico de postagem dos Correios).",
                "Envie ao cliente o código dos Correios com as instruções de embalagem (pode usar a mesma caixa do envio original).",
                "Solicite que o cliente inclua uma cartinha dentro da caixa com: Nome completo, Número do pedido e Descrição do serviço solicitado.",
                "Registre tudo nas notas internas da ISET com data, motivo e código de rastreio."
              ],
              critical_points: "Nunca gere reversa sem solicitar a cartinha explicativa dentro da caixa; caixas sem identificação atrasam dias na triagem.",
              expected_result: "Reversa gerada, cliente orientado e sistema atualizado com rastreabilidade total."
            },
            checklist: [
              "Verificou se está dentro do prazo dos 60 dias?",
              "Gerou a etiqueta/código de logística reversa?",
              "Instruiu o cliente a colocar a carta explicativa com o número do pedido na caixa?",
              "Registrou o histórico detalhado na ISET?"
            ]
          }
        ]
      },
      {
        id: "atend-etapa-3",
        number: "03",
        title: "Logística Reversa & Repostagens",
        description: "Recepção de pacotes devolvidos, triagem com carta, conferência na ISET e encaminhamento para a oficina.",
        aulas: [
          {
            id: "atend-3-1",
            code: "AULA 3.1",
            title: "Recepção, Triagem e Conferência de Devoluções",
            estimated_minutes: 15,
            youtube_id: "",
            key_points: [
              "Abra o pacote com cuidado e procure imediatamente pela carta do cliente ou certificado com o nº do pedido.",
              "Pesquise o pedido na ISET para validar se foi logística reversa, modelo solicitado e endereço.",
              "Envie mensagem padrão de confirmação de recebimento no WhatsApp do cliente.",
              "Confirme o endereço de reenvio antes de despachar a peça para a fábrica."
            ],
            content: `
### Procedimento de Chegada de Mercadoria de Retorno
Assim que as caixas de devolução chegarem dos Correios:

1. **Abertura e Busca de Identificação:**
   - Procure a carta manuscrita ou o certificado de garantia onde consta o número do pedido e o relato do que precisa ser feito (ajuste de aro, reposição de pedra, solda, etc.).
   - Se a caixa não tiver carta nem certificado, procure o nome do remetente na etiqueta externa dos Correios e faça a busca na ISET.

2. **Conferência de Dados no Sistema ISET:**
   - Verifique o modelo exato da peça.
   - Verifique se o frete foi cortesia (reversa nossa) ou pago pelo cliente.
   - Confira o histórico de anotações internas.

3. **Comunicação Imediata com o Cliente:**
   - Dispare a mensagem oficial no WhatsApp do cliente:
   \`\`\`text
   "Olá, boa tarde! Tudo bem? Passando para informar que recebemos a sua caixinha e já demos o devido andamento à peça que nos foi enviada para [trabalho a ser feito: ajuste de aro / reposição de pedra].
   Gostaria de aproveitar e confirmar com você o seu endereço completo de entrega: [Endereço da ISET]. Está correto?"
   \`\`\`
            `,
            pop: null,
            checklist: [
              "Abriu a caixa e localizou a carta/certificado do cliente?",
              "Consultou o pedido na ISET e checou o histórico?",
              "Enviou a mensagem padrão de recebimento no WhatsApp?",
              "Confirmou o endereço completo para a futura devolução?"
            ]
          },
          {
            id: "atend-3-2",
            code: "AULA 3.2",
            title: "Triagem para Oficina, Refação e Controle de Retorno",
            estimated_minutes: 20,
            youtube_id: "",
            key_points: [
              "Refação total: quando a peça está oxidada em nível irreversível, amassada, rachada ou trincada.",
              "Ajuste simples: alteração de até 2 números de aro na mesma peça.",
              "Controle da Caixa Preta: se o pedido for um combo de peças e apenas uma retornou, guarde com etiqueta na caixa preta até a fábrica liberar.",
              "Tolerância aceitável de aro: até 0,5 número de diferença é aceitável pelo toque na aneleira."
            ],
            content: `
### Critérios de Triagem: Oficina vs Refação
- **Refazemos a peça do zero quando:** Ela estiver muito oxidada, amassada, quebrada ou se o ajuste exigido for superior a 2 números de aro (esticar mais de 2 números enfraquece a estrutura do anel).
- **Ajustamos a peça existente quando:** Trata-se de reposição de pedrinha solta, polimento de limpeza ou ajuste de até 2 números de aro.

### O Fluxo com a Fábrica:
1. Coloque a joia no saquinho plástico transparente de serviço acompanhado da ficha com o número do pedido, serviço a ser feito e aro desejado.
2. Encaminhe para a bancada da oficina.
3. Se for pedido com combo (ex: par de alianças onde só uma precisou de ajuste), a peça que não precisou de conserto fica armazenada na **Caixa Preta** devidamente etiquetada.
4. Quando a fábrica devolver a peça pronta: confira o aro no bastão medidor, verifique a limpeza e coloque na fila de expedição.
            `,
            pop: {
              tag: "POP REPOST-01",
              title: "Recebimento, Triagem e Devolução de Repostagens",
              when_to_use: "Em todas as caixas de devolução/troca que chegarem na empresa.",
              responsible: "Responsável por Repostagens / Atendimento",
              tools: ["Estilete", "Bastão medidor de anel", "Sistema ISET", "Saquinhos de serviço"],
              steps: [
                "Abra o pacote com cuidado e retire a carta e a joia.",
                "Meça o aro da peça no bastão medidor e compare com o que foi solicitado na carta.",
                "Abra o pedido na ISET, confira os dados e adicione nova nota com a data de entrada na oficina.",
                "Envie a mensagem de confirmação ao cliente no WhatsApp.",
                "Em caso de cobrança (fora de garantia ou pedra perdida), envie a chave PIX e aguarde o comprovante.",
                "Separe a peça no saquinho identificado e leve até a caixa de entrada da oficina.",
                "Ao receber de volta da fábrica: faça a conferência dimensional no medidor antes de embalar."
              ],
              critical_points: "Nunca devolva uma aliança para o cliente sem medir novamente no bastão após sair da oficina.",
              expected_result: "Peça consertada no prazo, cliente informado em todas as etapas e histórico documentado."
            },
            checklist: [
              "Identificou se o caso é refação total ou apenas ajuste simples?",
              "Se houver taxa a cobrar, o comprovante do PIX foi anexado?",
              "A peça foi entregue à oficina com identificação legível?",
              "Na saída da oficina, conferiu o número do aro no bastão medidor?"
            ]
          }
        ]
      },
      {
        id: "atend-etapa-4",
        number: "04",
        title: "Embalagem, Kits de Entrega & Expedição",
        description: "Padrão de caixas (pequena vs grande), enxoval por categoria (capelo, diploma, flanela, bombom) e etiquetagem.",
        aulas: [
          {
            id: "atend-4-1",
            code: "AULA 4.1",
            title: "Padrão de Embalagens e Montagem de Caixas",
            estimated_minutes: 15,
            youtube_id: "",
            key_points: [
              "Caixa Pequena: para pedidos de apenas 1 peça individual.",
              "Caixa Grande: para pedidos com 2 ou mais peças (combos de alianças, formatura + pingente).",
              "Papel de Seda: forrar o fundo da caixa, acomodar o kit e lacrar no topo com o adesivo Sempre Joias.",
              "Saco de tecido protetor: todas as peças devem ser colocadas no saquinho de tecido."
            ],
            content: `
### O Ritual do Unboxing da Sempre Joias
A experiência de abrir a caixa da Sempre Joias é parte fundamental do encantamento do cliente. Toda caixa deve sair perfeita, sem amassados, manchas ou itens esquecidos.

#### TAMANHO DAS CAIXAS DE CORREIOS:
- **Caixa Pequena:** Utilizada para 1 peça única (ex: um solitário, um anel individual, uma medalha).
- **Caixa Grande:** Utilizada para 2 ou mais peças (ex: par de alianças, combo de anel + pingente, kits familiares).

#### RITUAL DE MONTAGEM:
1. Monte a caixa de papelão kraft sem vincos forçados.
2. Forre o interior com a folha de **papel de seda**, cobrindo o fundo e deixando as abas para fora.
3. Posicione o kit do produto de acordo com a sua categoria (ver Aula 4.2).
4. Dobre as abas do papel de seda por cima de todo o conteúdo.
5. Coloque o **Certificado de Garantia** por cima.
6. Lacrar com o **adesivo redondo oficial da Sempre Joias**.
            `,
            pop: null,
            checklist: [
              "Escolheu a caixa correta (pequena para 1 peça, grande para combo)?",
              "Forrou a caixa com o papel de seda perfeitamente dobrado?",
              "Colocou todas as peças dentro dos seus respectivos saquinhos de tecido?",
              "Fechou o papel de seda com o adesivo oficial Sempre Joias?"
            ]
          },
          {
            id: "atend-4-2",
            code: "AULA 4.2",
            title: "Enxoval Obrigatório por Categoria de Joia",
            estimated_minutes: 20,
            youtube_id: "",
            key_points: [
              "Formaturas: peça no capelo, diploma do curso, flanela mágica, bombom e garantia.",
              "Sacro: caixa aveludada, flanela mágica, bombom e garantia (NÃO leva diploma).",
              "Alianças e Namoro: caixa aveludada de anel duplo, flanela mágica, bombom e garantia.",
              "Berloques: cartela plástica lacrada, saquinho de tecido, flanela e garantia.",
              "Aço Cirúrgico: NÃO acompanha flanela mágica (a prata oxida, o aço não)."
            ],
            content: `
### Checklist de Itens por Categoria de Produto

| Categoria | Embalagem Principal | Itens Adicionais no Pacote | Observações Especiais |
|---|---|---|---|
| **Formatura** (Anel, Pingente, Berloque) | **Capelo** de veludo | Diploma do curso + Flanela Mágica + Bombom + Garantia | O diploma deve ter o nome do curso impresso correto |
| **Sacro** (Medalhas, Escapulários) | Caixa aveludada retangular | Flanela Mágica + Bombom + Certificado de Garantia | **NÃO** acompanha diploma |
| **Alianças & Namoro** | Caixa aveludada quadrada (dupla) | Flanela Mágica + Bombom + Certificado de Garantia | Garantir que ambos os aros estejam no mesmo estojo |
| **Berloques Individuais** | Cartela no plástico lacrado | Saquinho de tecido + Flanela + Certificado | Proteger contra atrito |
| **Peças em Aço Cirúrgico** | Caixa padrão / estojo | Bombom + Certificado de Garantia | **NÃO** colocar flanela mágica |

> ⚠️ **Atenção ao Bombom:** O bombom é uma cortesia afetiva. Em dias de calor extremo, certifique-se de que o bombom esteja firme e sem vazamento de chocolate.
            `,
            pop: {
              tag: "POP EXP-01",
              title: "Conferência de Enxoval e Fechamento de Caixa",
              when_to_use: "Em todas as expedições de pedidos para correios, motoboy ou retirada.",
              responsible: "Operador de Embalagem & Expedição",
              tools: ["Caixas P/G", "Papel seda", "Adesivos", "Estojos/Capelos", "Flanelas", "Bombons", "Diplomas"],
              steps: [
                "Pegue a ordem de pedido impressa e confira o modelo, banho e gravação da peça.",
                "Selecione o estojo correto conforme a categoria (Capelo para formatura, aveludada para alianças/sacro).",
                "Acomode a joia no estojo com firmeza.",
                "Adicione a flanela mágica e o bombom cortesia.",
                "Se for formatura, dobre e insira o mini-diploma correspondente ao curso.",
                "Insira o certificado de garantia datado.",
                "Dobre o papel de seda e lacre com o adesivo Sempre Joias.",
                "Feche a caixa externa de papelão."
              ],
              critical_points: "Nunca esqueça o diploma em pedidos de formatura nem envie flanela em peças de aço cirúrgico.",
              expected_result: "Caixa 100% montada dentro dos mais altos padrões visuais e sensoriais da marca."
            },
            checklist: [
              "Verificou se a peça é formatura e incluiu o capelo + diploma?",
              "Colocou o bombom e a flanela mágica (exceto aço)?",
              "O certificado de garantia está assinado/datado?",
              "O papel de seda está limpo e bem posicionado?"
            ]
          },
          {
            id: "atend-4-3",
            code: "AULA 4.3",
            title: "Etiquetagem, Modalidades de Envio e Despacho",
            estimated_minutes: 15,
            youtube_id: "",
            key_points: [
              "Correios: etiqueta de transporte na frente, Nota Fiscal dobrada no saco plástico no verso.",
              "Motoboy: etiqueta simplificada contendo nome, telefone, endereço com complemento e ponto de referência.",
              "Retirada no Showroom: peça em saco de tecido dentro da sacola kraft (não precisa de caixa de correios).",
              "Peças de Manutenção: saquinho plástico lacrado com cartela explicativa."
            ],
            content: `
### Modalidades de Envio

#### 1. Envio por Correios (SEDEX / PAC)
- **Frente da caixa:** Cole a etiqueta de frete dos Correios bem centralizada, sem tampar o código de barras com fita adesiva opaca.
- **Verso da caixa:** A Nota Fiscal (ou Declaração de Conteúdo) deve ser dobrada com o cabeçalho visível, inserida no envelope plástico transparente autoadesivo (canguru) e colada no verso.

#### 2. Envio por Motoboy
- A embalagem interna é a mesma caixa bonita.
- Na parte externa, cole a **Etiqueta de Motoboy** contendo: Nome do cliente, telefone com WhatsApp, endereço completo com número e complemento, e instruções especiais de entrega.

#### 3. Retirada no Showroom
- Não é necessário gastar caixa de papelão de Correios.
- Acomode a peça em seu estojo/capelo dentro do saquinho de tecido e coloque dentro da **Sacola Oficial da Sempre Joias** com papel de seda e laço.
            `,
            pop: null,
            checklist: [
              "A etiqueta dos Correios está nítida e com código de barras legível?",
              "A Nota Fiscal está no envelope canguru no verso da caixa?",
              "Em envios de motoboy, o telefone de contato está correto?",
              "Confirmou a baixa no sistema de expedição?"
            ]
          }
        ]
      }
    ]
  },

  fabrica: {
    id: "fabrica",
    title: "Fábrica, Oficina & Equipamentos",
    shortTitle: "Fábrica & Produção",
    icon: "Factory",
    color: "#10B981",
    badgeColor: "bg-emerald-100 text-emerald-800 border-emerald-200",
    description: "Do arquivo 3D à peça finalizada: parâmetros de impressão 3D, troca de FEP, pré-cravação, rolo centrífugo, solda TIG de escapulários, inspeção na lupa, manutenção da bomba e esteira de produção.",
    etapas: [
      {
        id: "fab-etapa-1",
        number: "01",
        title: "Conhecendo as Joias & Matérias-Primas",
        description: "Catálogo de solitários, anéis de formatura, medidas e perfis de alianças, lapidações e guia de pedras.",
        aulas: [
          {
            id: "fab-1-1",
            code: "AULA 1.1",
            title: "Modelos de Joias e Nomenclatura de Fábrica",
            estimated_minutes: 20,
            youtube_id: "",
            key_points: [
              "Diferencie Solitários, Anéis de Formatura, Alianças, Escapulários e Pingentes.",
              "Modelos emblemáticos de formatura: Atlas, Triunfo V4, Vega.",
              "Entenda a diferença entre peças fundidas inteiriças e peças montadas com solda (escapulários)."
            ],
            content: `
### O Catálogo da Fábrica
Na oficina da Sempre Joias, cada joia possui um método de produção específico:
- **Solitários:** Joias delicadas com garra central, exigem atenção extrema na cravação e pré-cravação para não entortar as garras.
- **Anéis de Formatura:** Peças robustas com plaquinhas de símbolos nos dois lados e uma pedra central (ou pavê de pedras ao redor).
- **Escapulários:** Duas medalhas com corrente veneziana ou elos, montadas manualmente na solda TIG.
- **Medalhas e Pingentes:** Fundição direta com argola reforçada.
            `,
            pop: null,
            checklist: [
              "Conhece a diferença entre os modelos Atlas, Triunfo e Vega?",
              "Sabe identificar as plaquinhas de símbolos no arquivo 3D?",
              "Sabe quais peças exigem solda e quais são fundição direta?"
            ]
          },
          {
            id: "fab-1-2",
            code: "AULA 1.2",
            title: "Catálogo de Alianças: Larguras, Perfis e Modelos",
            estimated_minutes: 25,
            youtube_id: "",
            key_points: [
              "Larguras padrão: 3mm, 3.5mm, 4mm, 5mm, 5.5mm e 6mm.",
              "Perfis: Anatômico (conforto interno abaulado) vs Reto/Tradicional.",
              "Modelos principais: Player (1 e 2), Akai Ito (1 fio, duplo e grande), Celta, Triquetra, Vital, Noite Estrelada e Pequeno Príncipe."
            ],
            content: `
### Guia de Produção de Alianças
As alianças são o carro-chefe da empresa e exigem rigor milimétrico no acabamento e na gravação interna.

#### PRINCIPAIS MODELOS PRODUZIDOS:
- **Linha Player:** 3mm e 5mm retos ou anatômicos (marcações Player 1 / Player 2).
- **Linha Akai Ito:** Fio vermelho resinado ou baixo relevo gravado (3mm, 3.5mm, 5mm e fio duplo).
- **Linha Celta & Triquetra:** Entalhes profundos em baixo relevo nórdico (4mm, 5mm e 5.5mm).
- **Linha Vital:** 4mm com solitário acoplado ou versão solo.
- **Linha Noite Estrelada:** Texturização diamantada artística.
- **Linha Pequeno Príncipe:** 5mm e 6mm com solitário raposa.
            `,
            pop: null,
            checklist: [
              "Sabe identificar visualmente a largura da aliança pelo paquímetro?",
              "Sabe diferenciar o acabamento reto do anatômico?",
              "Conhece a lista de modelos mais solicitados?"
            ]
          },
          {
            id: "fab-1-3",
            code: "AULA 1.3",
            title: "Lapidações, Zircônias e Guia de Pedras",
            estimated_minutes: 20,
            youtube_id: "",
            key_points: [
              "Lapidações usadas na fábrica: Brilhante (redonda), Navete, Baguete, Gota, Oval e Princesa (quadrada).",
              "Zircônias cúbicas: alta resistência ao calor para fundição direta (pré-cravação).",
              "Organização do gaveteiro de pedras: divisão por milímetros (1.0mm, 1.25mm, 1.5mm, 2.0mm, etc.) e cores."
            ],
            content: `
### As Pedras e Lapidações
Para a pré-cravação na cera, as pedras devem resistir à temperatura do gesso e do forno de fundição.
- **Brilhante:** Redonda multifacetada, usada em anéis de formatura e solitários.
- **Navete & Gota:** Usadas em modelos florais e laterais de joias nobres.
- **Baguete:** Lapidação retangular usada em solitários contemporâneos.

> ⚠️ **Atenção:** Nunca misture os pacotes de pedras por milimetragem no gaveteiro. Uma pedra de 1.25mm colocada na cavidade de 1.5mm causará soltura imediata na fundição.
            `,
            pop: null,
            checklist: [
              "Sabe diferenciar as lapidações brilhante, navete, baguete e gota?",
              "Sabe medir o diâmetro da pedra no calibrador de gemas?",
              "Mantém o gaveteiro de pedras rigorosamente organizado por milímetro?"
            ]
          }
        ]
      },
      {
        id: "fab-etapa-2",
        number: "02",
        title: "Impressão 3D, Nivelamento & Pré-Cravação",
        description: "Limpeza de tanques, troca de película FEP, nivelamento do eixo Z e fixação de pedras na resina/cera.",
        aulas: [
          {
            id: "fab-2-1",
            code: "AULA 2.1",
            title: "Limpeza e Cura das Peças Pós-Impressão 3D",
            estimated_minutes: 25,
            youtube_id: "",
            key_points: [
              "Sempre utilize luvas nitrílicas durante todo o processo.",
              "Ciclo triplo de lavagem: Álcool sujo (10 min) -> Álcool meio-sujo -> Álcool limpo no ultrassom.",
              "Secagem com compressor de ar: distância MÍNIMA de 15 cm (se bater perto a peça voa e quebra).",
              "Retirar as peças da plataforma no tapetinho azul usando o lado chanfrado da espátula."
            ],
            content: `
### Procedimento de Limpeza e Pós-Impressão
A resina calcinável (cera líquida) exige remoção perfeita de todo o excesso de líquido antes da cura UV. Resina acumulada nos cantos estraga os detalhes dos símbolos da formatura.

#### O Risco Crítico do Vento do Compressor:
Ao secar a bandeja com a pistola de ar comprimido, mantenha **no mínimo 15 cm de distância**. As peças de cera impressas ainda estão cruas e extremamente frágeis. O vento forte direto pode arremessar a peça contra a parede ou trincar as garras finas.
            `,
            pop: {
              tag: "POP 2.1",
              title: "Limpeza e Cura das Peças Após a Impressão 3D",
              when_to_use: "Logo após o término de cada ciclo de impressão 3D.",
              responsible: "Operador de Impressão 3D",
              tools: ["Luvas nitrílicas", "Banho ultrassom", "3 potes de álcool isopropílico", "Compressor de ar", "Espátula de inox chanfrada", "Tapete de corte azul"],
              steps: [
                "Coloque as luvas de proteção.",
                "Retire a tampa da impressora 3D.",
                "Solte o manípulo superior e retire a plataforma de impressão.",
                "Posicione a plataforma no 1º pote (Álcool Sujo). Tampe o pote.",
                "Coloque o pote no ultrassom por um ciclo de 10 minutos.",
                "TAMPE a impressora imediatamente para proteger a resina do tanque contra a luz ambiente.",
                "Ao terminar o ciclo, transfira a plataforma para o 2º pote (Álcool Meio-Sujo) e rode mais um ciclo.",
                "Transfira para o 3º pote (Álcool Limpo) para o enxágue final.",
                "Ligue o compressor de ar.",
                "Retire a plataforma do álcool e bata vento com a pistola a uma distância mínima de 15 cm até as peças ficarem totalmente foscas e secas.",
                "Leve a plataforma para o tapetinho azul da bancada.",
                "Utilize a espátula de inox pelo lado chanfrado com movimento firme e rasante para descolar as peças da base.",
                "Guarde a plataforma limpa ao lado da impressora."
              ],
              critical_points: "Distância mínima de 15 cm ao bater vento com compressor. Nunca deixe a impressora destampada.",
              expected_result: "Peças completamente foscas, secas, sem brilho de resina líquida e descoladas intactas da base."
            },
            checklist: [
              "Usou luvas durante todo o manuseio?",
              "Passou pelos 3 estágios de lavagem de álcool?",
              "Manteve a distância de 15 cm ao bater o ar comprimido?",
              "As peças descoladas foram inspecionadas quanto a quebras?"
            ]
          },
          {
            id: "fab-2-2",
            code: "AULA 2.2",
            title: "Troca do Filme FEP e Nivelamento da Plataforma",
            estimated_minutes: 30,
            youtube_id: "",
            key_points: [
              "Válido para impressoras Elegoo Mars 4 Ultra e Saturn 3.",
              "Troque o FEP quando notar furos, vincos esbranquiçados ou perda de tensão.",
              "Aperto em cruz dos parafusos do aro para garantir tensão uniforme do filme.",
              "Nivelamento com folha sulfite: soltar parafusos do eixo Z, descer ao Home, apertar parafusos e definir Z=0."
            ],
            content: `
### 1. Quando Trocar o FEP?
O filme FEP fica no fundo do tanque de resina. Ele deve ser substituído quando:
- Apresentar manchas brancas opacas permanentes.
- Sofrer arranhões ou furos microscópicos que provoquem vazamento de resina sobre a tela LCD.
- As impressões começarem a falhar no mesmo quadrante por falta de descolamento.

### 2. Nivelamento da Plataforma
Um nivelamento incorreto faz com que as peças soltem da plataforma no meio da impressão. O teste da folha de papel sulfite garante a calibragem correta da folga do eixo Z.
            `,
            pop: {
              tag: "POP 2.2 / 2.3",
              title: "Troca de Filme FEP e Nivelamento do Eixo Z",
              when_to_use: "FEP danificado ou após qualquer troca de tanque/desalinhamento de plataforma.",
              responsible: "Operador de Impressão 3D / Manutenção",
              tools: ["Chave Allen correspondente", "Filme FEP novo", "Espuma/apoio de garrafa", "Folha de papel sulfite A4", "Pano de microfibra"],
              steps: [
                "Esvazie a resina do tanque com filtro e limpe totalmente com álcool.",
                "Vire o tanque de cabeça para baixo e solte todos os parafusos do aro externo.",
                "Retire o aro interno e descarte o FEP velho.",
                "Posicione o FEP novo entre as molduras, deixando uma folga central apoiada na tampinha para calibrar a tensão.",
                "Aperte os parafusos em padrão de cruz (diagonais opostas) para esticar de maneira homogênea.",
                "Corte as rebarbas do FEP com estilete rente à moldura.",
                "Para nivelar: afrouxe os 2 parafusos laterais da plataforma de impressão.",
                "Coloque uma folha sulfite A4 sobre a tela LCD da impressora.",
                "Aperte o botão HOME no painel da impressora para descer o eixo Z.",
                "Pressione a plataforma suavemente com a mão espalmada para baixo e aperte os parafusos laterais firmemente.",
                "Tente puxar a folha: ela deve oferecer resistência uniforme em todos os lados sem rasgar.",
                "Clique em 'Set Z=0' no menu da máquina para salvar a calibragem."
              ],
              critical_points: "Não aperte parafusos em sequência circular; sempre aperte em cruz para não enrugar o FEP.",
              expected_result: "FEP esticado como tambor e plataforma perfeitamente paralela à tela LCD."
            },
            checklist: [
              "FEP esticado uniformemente sem rugas?",
              "Parafusos apertados em cruz com torque adequado?",
              "Folha sulfite com tração homogênea em todos os cantos?",
              "Parâmetro Z=0 salvo na memória da impressora?"
            ]
          },
          {
            id: "fab-2-3",
            code: "AULA 2.3",
            title: "Pré-Cravação na Cera & Ordem de Prioridade",
            estimated_minutes: 25,
            youtube_id: "",
            key_points: [
              "Preparação de bancada: potinho de glicerina e calcador de pedras de aço inox.",
              "Ordem de prioridade (do mais difícil para o mais fácil): Atlas -> Pedras brancas do Triunfo V4 -> Pedras brancas gerais -> Pedras coloridas.",
              "Glicerina serve para fixar a pedra suavemente na ponta do aplicador sem engordurar a cera.",
              "Acomode a pedra na cavidade e pressione o calcador nas garras com firmeza perpendicular."
            ],
            content: `
### O que é a Pré-Cravação?
Na Sempre Joias, as pedras de zircônia são inseridas diretamente na peça de cera antes da árvore de fundição. O metal líquido entra na fundição e já abraça a pedra, garantindo velocidade e precisão.

#### Sequência de Dificuldade e Prioridade:
1. **Modelo Atlas:** Garras delicadas e posicionamento milimétrico — requer máxima atenção.
2. **Triunfo V4 (Pedras Brancas Centrais):** Pavê de pedras com encaixe rente.
3. **Demais Anéis com Pedras Brancas:** Anéis de formatura tradicionais.
4. **Pedras Coloridas:** Zircônias com tratamentos térmicos específicos.
            `,
            pop: null,
            checklist: [
              "Bancada limpa com potinho de glicerina e calcador correto?",
              "Respeitou a ordem de prioridade de cravação?",
              "Verificou se a mesa da pedra ficou perfeitamente nivelada no aro?",
              "As garras cobrem a cintura da pedra sem esmagar a cera?"
            ]
          }
        ]
      },
      {
        id: "fab-etapa-3",
        number: "03",
        title: "Acabamento & Processamento Mecânico",
        description: "Operação do rolo centrífugo, troca de chip da máquina de polimento, secador acelerado e compostos químicos.",
        aulas: [
          {
            id: "fab-3-1",
            code: "AULA 3.1",
            title: "Troca do Chip e Retirada de Peças da Máquina",
            estimated_minutes: 20,
            youtube_id: "",
            key_points: [
              "Máquina de acabamento rotativo/centrífugo opera com controle de ciclos via chip eletrônico.",
              "Quando o ciclo terminar: desligue o comando elétrico antes de abrir a cuba.",
              "Substitua o chip esgotado pelo novo e confirme o reset do horímetro.",
              "Peneire as peças sobre o balde receptor com água corrente para retirar todo o resíduo abrasivo."
            ],
            content: `
### A Máquina de Rolo Centrífugo
A máquina de acabamento realiza a primeira quebra de aspereza da fundição. Ela utiliza palitos ou pastilhas de cerâmica/resina misturadas a sabão especial.

#### Cuidados na Troca do Chip:
- O chip é o dispositivo de liberação de tempo da máquina.
- Nunca force o conector do chip; insira no sentido correto.
- Anote o número de lote do chip trocado na planilha de controle de consumo da oficina.
            `,
            pop: {
              tag: "POP 3.1",
              title: "Troca de Chip e Operação da Máquina de Polimento",
              when_to_use: "A cada término de ciclo de acabamento ou indicação de chip expirado no painel.",
              responsible: "Operador de Acabamento / Polidor",
              tools: ["Chip novo", "Peneira de inox", "Balde de decantação", "Água corrente"],
              steps: [
                "Desligue a rotação da máquina e aguarde a parada completa dos rotores.",
                "Retire o chip usado do soquete e insira o novo chip homologado.",
                "Confirme no display que o tempo de ciclo foi reiniciado.",
                "Abra a tampa da cuba e despeje a mistura de peças e mídia sobre a peneira.",
                "Lave abundantemente em água corrente para eliminar a pasta de polimento cinza.",
                "Separe as joias manualmente para a bandeja de secagem."
              ],
              critical_points: "Não ligue a máquina sem conferir se a tampa da cuba está travada com o pino de segurança.",
              expected_result: "Máquina com chip ativo e peças sem resíduo de pasta, prontas para o secador."
            },
            checklist: [
              "Máquina desligada antes de mexer na cuba?",
              "Chip novo inserido e reconhecido pelo painel?",
              "Peças lavadas sem restos de pasta abrasiva?",
              "Pino de trava da tampa checado?"
            ]
          },
          {
            id: "fab-3-2",
            code: "AULA 3.2",
            title: "Retirar e Lavar Escapulários do Rolo",
            estimated_minutes: 15,
            youtube_id: "",
            key_points: [
              "Escapulários possuem correntes finas que podem enroscar nos rotores.",
              "As correntes devem estar obrigatoriamente travadas para não embolar.",
              "Enxágue com água morna e xampu neutro para soltar mídias presas nos elos.",
              "Verifique se as duas plaquinhas estão íntegras na mesma peça."
            ],
            content: `
### Procedimento com Escapulários no Rolo
O escapulário é uma peça crítica no acabamento mecânico:
- Por ter corrente acoplada, o operador **nunca** deve soltar a trava da corrente antes do banho de ouro/prata.
- Ao retirar da máquina, examine os elos da corrente contra a luz: palitinhos de mídia de acabamento podem ficar presos dentro dos elos. Use a pinça para remover qualquer corpo estranho.
            `,
            pop: {
              tag: "POP 3.2",
              title: "Lavagem e Separação de Escapulários",
              when_to_use: "Após o término da rotação de escapulários na máquina centrífuga.",
              responsible: "Operador de Acabamento",
              tools: ["Peneira plástica", "Pinça fina", "Bandeja branca"],
              steps: [
                "Peneire os escapulários sobre a água.",
                "Inspecione elo por elo para checar se há pastilha abrasiva presa.",
                "Mantenha as correntes esticadas na bandeja para não criar nós.",
                "Leve imediatamente para o secador de bancada para evitar oxidação precoce."
              ],
              critical_points: "Não puxe a corrente se ela estiver embolada; solte com duas pinças com calma para não arrebentar.",
              expected_result: "Escapulários sem nós, com correntes íntegras e prontos para inspeção."
            },
            checklist: [
              "Verificou se há mídias de acabamento presas nos elos?",
              "Correntes desemaranhadas sem puxar com força?",
              "Ambas as medalhas conferidas em cada escapulário?"
            ]
          },
          {
            id: "fab-3-3",
            code: "AULA 3.3",
            title: "Secagem Acelerada de Peças e Atualização de Compostos",
            estimated_minutes: 15,
            youtube_id: "",
            key_points: [
              "Secador térmico de bancada elimina manchas de gotas de água antes do banho e da inspeção.",
              "Tempo de secador: 3 a 5 minutos dependendo da quantidade de peças.",
              "Atualização de compostos: proporção correta de detergente lubrificante e pó de polimento."
            ],
            content: `
### O Secador de Bancada
Após saírem da lavagem úmida, as peças não podem secar ao ar livre porque o cloro e minerais da água deixam manchas opacas (water spots) no metal polido.
- Acomode as peças na grelha do secador térmico.
- Ligue o fluxo de ar quente.
- Mantenha até que o metal atinja temperatura morna e brilho uniforme.
            `,
            pop: null,
            checklist: [
              "Peças espalhadas sem sobreposição na grelha?",
              "Tempo de secagem cumprido?",
              "Superfícies conferidas contra manchas de água?"
            ]
          }
        ]
      },
      {
        id: "fab-etapa-4",
        number: "04",
        title: "Soldagem TIG & Bancada de Joalheria",
        description: "Organização da bancada, preparação do equipamento de micro-solda TIG e soldagem de argolas de escapulários.",
        aulas: [
          {
            id: "fab-4-1",
            code: "AULA 4.1",
            title: "Organização da Bancada de Micro-Solda e Ferramentas",
            estimated_minutes: 20,
            youtube_id: "",
            key_points: [
              "Ferramentas essenciais: Alicate meia-cana sem estrias, pinça de titânio, bloco refratário e eletrodo de tungstênio.",
              "A ponta do eletrodo de tungstênio deve estar perfeitamente afiada em ângulo de 30°.",
              "Eletrodo sujo ou com prata fundida na ponta causa faíscas erráticas e buracos na joia.",
              "Ajuste da potência do TIG de acordo com a espessura do elo da corrente."
            ],
            content: `
### A Bancada de Micro-Solda TIG
A soldagem TIG por arco pulsado substitui o maçarico tradicional na união de elos finos e escapulários. Ela oferece solda limpa, sem queimar as pedras já cravadas e sem escurecer excessivamente a prata.

#### Cuidados com o Tungstênio:
- A cada 20 soldas (ou se o tungstênio encostar na prata e grudar), desligue a máquina e afie a ponta do eletrodo no rebolo diamantado.
- Uma ponta cega dissipa o arco e derrete a medalha ao invés de soldar a argola.
            `,
            pop: null,
            checklist: [
              "Eletrodo de tungstênio está afiado como agulha?",
              "Gás de proteção (Argônio) com pressão correta no manômetro?",
              "Pinça de aterramento posicionada firmemente na peça?"
            ]
          },
          {
            id: "fab-4-2",
            code: "AULA 4.2",
            title: "Soldagem TIG de Escapulários e Argolas",
            estimated_minutes: 25,
            youtube_id: "",
            key_points: [
              "Alinhamento perfeito da fresta da argola com o alicate meia-cana antes do disparo.",
              "A argola deve ficar perfeitamente fechada ('topo com topo'), sem vão visível.",
              "O disparo do pulso TIG deve ser direcionado exatamente sobre a fenda.",
              "Teste de tração manual leve após o esfriamento para confirmar a fusão do metal."
            ],
            content: `
### Procedimento de Solda de Escapulário
Conforme documento técnico \`Escapulario _260528_124022.pdf\` e transcrição Whisper do vídeo:

1. **Alinhamento do Elo:**
   - Com o alicate meia-cana, feche a argolinha que conecta a corrente à medalha.
   - Os dois topos do corte devem se tocar perfeitamente. Se houver vão de ar, o pulso elétrico vai abrir um buraco em vez de fundir.

2. **O Ponto de Solda:**
   - Posicione o eletrodo a 0.5 mm da junção (sem encostar!).
   - Pressione o pedal de disparo.
   - Um flash suave com gás de argônio fundirá as duas pontas instantaneamente em uma esfera homogênea.

3. **Conferência de Resistência:**
   - Puxe a corrente com os dedos aplicando leve tração. A solda não pode se abrir.
            `,
            pop: {
              tag: "POP 5.1",
              title: "Soldagem de Escapulários na Máquina TIG",
              when_to_use: "Montagem final de escapulários e união de elos de corrente.",
              responsible: "Operador de Solda / Ourives de Bancada",
              tools: ["Máquina de solda TIG com pedal", "Alicate de bico fino liso", "Pinça de titânio cruzada", "Lupa de cabeça ou microscópio de bancada"],
              steps: [
                "Ligue a máquina TIG e o fluxo de gás argônio.",
                "Regule a potência do disparo conforme a espessura da argola (ex: 12 Joules / 3ms).",
                "Verifique a ponta do tungstênio; se estiver rombuda, afie antes de começar.",
                "Segure a argola com a pinça e feche a fenda com o alicate até encostar topo com topo.",
                "Posicione a fenda sob o eletrodo e aponte no centro da união.",
                "Dispare o pedal com um toque curto.",
                "Gire a argola 180° e examine se o ponto de fusão foi completo em ambos os lados.",
                "Faça o teste de tração leve com os dedos para certificar que a solda pegou por fusão real."
              ],
              critical_points: "Nunca solde com vão aberto na fenda da argola. O eletrodo nunca deve tocar na peça antes do disparo.",
              expected_result: "Argola perfeitamente fechada, cordão de solda liso e sem oxidação preta excessiva."
            },
            checklist: [
              "Fenda da argola totalmente fechada antes do disparo?",
              "Potência da máquina adequada à espessura do fio?",
              "Fez o teste de tração após soldar?",
              "Medalhas alinhadas de frente sem torção da corrente?"
            ]
          }
        ]
      },
      {
        id: "fab-etapa-5",
        number: "05",
        title: "Controle de Qualidade (QA) & Verificação na Lupa",
        description: "Inspeção visual minuciosa, critérios de tolerância para poros e gravação, e triagem de aprovação vs repolimento.",
        aulas: [
          {
            id: "fab-5-1",
            code: "AULA 5.1",
            title: "Inspeção Visual sob a Lupa: Alianças, Formaturas e Escapulários",
            estimated_minutes: 30,
            youtube_id: "",
            key_points: [
              "Ajuste da lupa de bancada: posicionar na altura dos olhos e ligar a lâmpada circular.",
              "Use SEMPRE papel toalha duplo para secar a peça e girar no dedo — nunca inspecione peça úmida (a água confunde com poros).",
              "Alianças: conferir aro externo (balançando contra a luz), laterais e nitidez da gravação interna.",
              "Formaturas: checar plaquinhas de símbolos (se faltar um traço, a peça volta) e firmeza de todas as pedras.",
              "Escapulários: regra do microporo (1 microporo invisível a olho nu passa; múltiplos microporos reprovam a peça)."
            ],
            content: `
### O Olho Clínico do Controle de Qualidade
O QA é a última barreira antes de a joia ir para o banho e para a caixa do cliente. Um erro que passa daqui vira reclamação no SAC.

#### 1. COMO MONTAR SEU POSTO DE INSPEÇÃO:
- Trave a haste da lupa na altura exata da sua cabeça para inspecionar sentado sem curvar as costas.
- Ligue a luz fluorescente/LED da lupa.
- Dobre uma folha de papel toalha em duas partes para que fique espessa.
- Encaixe o dedo indicador com o papel dentro do anel e o dedão por fora: gire a peça 360° para secar qualquer resíduo de água ou pó que possa criar ilusão de ótica.

#### 2. INSPEÇÃO DE ALIANÇAS:
- Gire a aliança sob a luz balançando suavemente no dedo: o reflexo da luz revela linhas de junção, riscos e porosidades ocultas.
- Olhe as duas laterais.
- Olhe o interior: a gravação interna deve estar funda e nítida. Gravação rasa ou quase sumindo **reprova** a peça.

#### 3. INSPEÇÃO DE ANÉIS DE FORMATURA:
- **Plaquinhas:** Dê zoom nas plaquinhas dos símbolos. Cada perninha, ramo de louro ou microscópio deve estar íntegro.
- **Pedras:** Verifique se todas as pedras do projeto estão presentes (ex: Triunfo tem várias pedras ao redor). Se faltar pedra, separe na pilha de cravação.

#### 4. AS TRÊS PILHAS DE SEPARAÇÃO:
1. **Aprovados:** Peças impecáveis que vão direto para a expedição/banho.
2. **Revisão / Retrabalho:** Peças com riscos ou poros que voltam para o polimento.
3. **Cravação:** Peças que soltaram pedras e voltam para o cravador.
            `,
            pop: {
              tag: "POP 4.1",
              title: "Verificação de Joias na Lupa de Bancada",
              when_to_use: "Em todas as peças finalizadas que saem do acabamento/polimento.",
              responsible: "Inspetor de Controle de Qualidade (QA)",
              tools: ["Lupa articulada de bancada com iluminação", "Papel toalha macio duplo", "Bandejas para 3 pilhas (Aprovado, Retrabalho, Cravação)"],
              steps: [
                "Ligue a iluminação da lupa e ajuste o foco na altura dos olhos.",
                "Dobre a folha de papel toalha e seque a joia totalmente por dentro e por fora.",
                "Segure a joia e balance-a sob o foco de luz para capturar reflexos.",
                "Examine o aro externo buscando microporos, trincas ou linhas de impressão.",
                "Examine o aro interno conferindo a profundidade da gravação a laser.",
                "Em anéis de formatura: use a lupa máxima para validar os traços das plaquinhas e a firmeza de cada pedra.",
                "Em escapulários: examine o relevo da imagem frontal e a lisura da chapa traseira.",
                "Destine a peça para a bandeja correta: Aprovada, Retrabalho ou Cravação."
              ],
              critical_points: "Nunca inspecione peças úmidas. Um traço rompido no símbolo da formatura é motivo de reprovação imediata.",
              expected_result: "100% dos defeitos retidos na fábrica, garantindo zero devoluções por defeito no SAC."
            },
            checklist: [
              "Peça foi 100% seca com papel duplo antes de inspecionar?",
              "Balançou a joia sob a luz para capturar reflexos de poros?",
              "Conferiu a nitidez e profundidade da gravação interna?",
              "Em formatura, todas as pedras estão firmes e presentes?"
            ]
          },
          {
            id: "fab-5-2",
            code: "AULA 5.2",
            title: "Atualização de Estágio e Rastreabilidade no Sistema",
            estimated_minutes: 15,
            youtube_id: "",
            key_points: [
              "Assim que o lote for inspecionado, atualize imediatamente o status no sistema de gestão.",
              "Peças aprovadas avançam para 'Pronto para Banho' ou 'Expedição'.",
              "Peças reprovadas devem ser apontadas com o motivo do desvio (ex: 'porosidade no aro', 'falha de gravação').",
              "A rastreabilidade correta alimenta a pontuação e os bônus dos operadores."
            ],
            content: `
### Rastreabilidade no Sistema de Gestão
Uma peça que passa na bancada física mas não é atualizada no sistema gera atraso na expedição e informação incorreta para o atendente do WhatsApp.
- Escaneie o código de barras ou pesquise o número do pedido.
- Avance o estágio para o próximo posto de trabalho.
- Em caso de refação, abra a ordem de retrabalho com o operador original responsável.
            `,
            pop: null,
            checklist: [
              "Escaneou o pedido no leitor de código de barras?",
              "Registrou o status correto no sistema?",
              "Se houve retrabalho, apontou o motivo específico?"
            ]
          }
        ]
      },
      {
        id: "fab-etapa-6",
        number: "06",
        title: "Manutenção Preventiva de Equipamentos",
        description: "Rotinas mensais da bomba de vácuo de fundição, purga diária do compressor de ar e troca de óleo.",
        aulas: [
          {
            id: "fab-6-1",
            code: "AULA 6.1",
            title: "Manutenção e Troca de Óleo da Bomba de Vácuo",
            estimated_minutes: 20,
            youtube_id: "",
            key_points: [
              "A bomba de vácuo é responsável por tirar as bolhas de ar do gesso da fundição.",
              "Verifique o nível do óleo pelo visor transparente: o óleo deve estar claro (âmbar translúcido).",
              "Se o óleo estiver leitoso, turvo ou escuro, troque imediatamente (água do gesso contaminou o óleo).",
              "Troca mensal obrigatória ou a cada 100 horas de fundição."
            ],
            content: `
### A Bomba de Vácuo da Fundição
Sem vácuo perfeito, o gesso fica com bolhas de ar e a joia fundida nasce cheia de bolinhas de prata que arruínam o trabalho.
- O óleo da bomba absorve umidade durante o ciclo de desaeração do gesso.
- **Visor de Nível:** O nível deve estar rigorosamente entre o traço mínimo e máximo do visor.
- Ao trocar o óleo, drene com a bomba ainda ligeiramente morna para que o óleo velho escorra completamente.
            `,
            pop: {
              tag: "POP 6.1",
              title: "Troca de Óleo da Bomba de Vácuo (Mensal)",
              when_to_use: "Mensalmente no primeiro dia útil do mês ou se o óleo estiver leitoso/escuro.",
              responsible: "Responsável de Manutenção / Fundidor",
              tools: ["Chave de bujão", "Óleo específico para bomba de vácuo (ISO VG 68 ou 100)", "Funil limpo", "Recipiente para descarte"],
              steps: [
                "Ligue a bomba por 2 minutos para aquecer o óleo e facilitar o escoamento.",
                "Desligue a bomba da tomada.",
                "Posicione o recipiente de coleta embaixo do dreno inferior.",
                "Abra o bujão de drenagem e deixe o óleo velho escorrer totalmente.",
                "Feche o bujão com a arruela de vedação.",
                "Abra a tampa superior de abastecimento.",
                "Com auxílio do funil, adicione o óleo novo para bomba de vácuo até atingir a metade do visor de vidro.",
                "Feche a tampa superior, limpe respingos com pano e ligue a máquina para conferir o som do motor."
              ],
              critical_points: "Nunca use óleo automotivo de motor; use apenas óleo técnico para bomba de vácuo.",
              expected_result: "Visor com óleo amarelo límpido e bomba alcançando vácuo máximo no manômetro em menos de 30 segundos."
            },
            checklist: [
              "Desconectou o plugue da tomada antes de drenar?",
              "Usou óleo técnico correto para bomba de vácuo?",
              "Nível posicionado exatamente entre as marcas do visor?",
              "Bujão bem apertado sem vazamento no piso?"
            ]
          },
          {
            id: "fab-6-2",
            code: "AULA 6.2",
            title: "Manutenção Diária e Purga do Compressor de Ar",
            estimated_minutes: 15,
            youtube_id: "",
            key_points: [
              "A purga de água da válvula inferior deve ser feita DIARIAMENTE ao final do expediente.",
              "Ar comprimido gera condensação de água: se não drenar, o reservatório enferruja por dentro e cospe água nas peças na bancada.",
              "Checagem semanal do nível de óleo do cárter e limpeza dos filtros de ar de sucção."
            ],
            content: `
### Cuidados com o Compressor de Ar
O compressor alimenta a secagem da resina 3D, a limpeza de bancada e os bicos de ar.
- **A água acumulada é o maior inimigo:** A umidade acumulada oxida o cilindro e sai em forma de spray pelas pistolas de ar, manchando peças recém-polidas.
- **Drenagem Diária:** Ao fim de cada dia, abra a torneira de purga embaixo do cilindro e deixe toda a água suja sair até sair apenas ar seco.
            `,
            pop: {
              tag: "POP 6.2",
              title: "Drenagem de Água e Checagem do Compressor de Ar",
              when_to_use: "Drenagem: Diária ao final do expediente. Troca de óleo: Mensal.",
              responsible: "Operador de Oficina / Manutenção",
              tools: ["Pano de chão", "Recipiente para água de purga", "Chave de nível"],
              steps: [
                "Desligue o botão geral do compressor.",
                "Coloque um balde ou pano embaixo da válvula de purga no fundo do reservatório.",
                "Abra lentamente a válvula de drenagem.",
                "Aguarde o sopro de ar expelir toda a água acumulada.",
                "Quando sair apenas ar seco, feche a válvula firmemente.",
                "Confira o visor de óleo do cárter (deve estar na marca central).",
                "Limpe a tela do filtro de ar com pincel."
              ],
              critical_points: "Abra a válvula devagar para a água sob pressão não respingar nos equipamentos vizinhos.",
              expected_result: "Reservatório sem água residual e pistolas soprando ar 100% seco na bancada."
            },
            checklist: [
              "Drenou a água do compressor hoje?",
              "O ar que sai na pistola da bancada está limpo e seco?",
              "O nível de óleo do motor está no centro do visor?"
            ]
          }
        ]
      },
      {
        id: "fab-etapa-7",
        number: "07",
        title: "Fluxo Operacional & Sistema CentralSJ",
        description: "A esteira de produção completa da Sempre Joias: status, prazos, passagens de bastão e cálculo de pontuação.",
        aulas: [
          {
            id: "fab-7-1",
            code: "AULA 7.1",
            title: "A Esteira de Produção e Estados do Pedido",
            estimated_minutes: 20,
            youtube_id: "",
            key_points: [
              "Fluxo linear da esteira: Aguardando Impressão -> Impresso -> Pré-Cravação -> Fundição -> Acabamento/Polimento -> Banho -> Controle de Qualidade -> Expedição.",
              "Cada operador é responsável por conferir a peça ANTES de aceitar a passagem de bastão.",
              "O registro correto do operador em cada etapa garante o cálculo fidedigno da pontuação e premiação mensal."
            ],
            content: `
### O Sistema Integrado CentralSJ
O sistema de gestão da Sempre Joias conecta os pedidos vendidos pelo comercial diretamente com as máquinas da fábrica.

#### O CICLO DE VIDA DE UM PEDIDO NA FÁBRICA:
1. **Aguardando Impressão:** Pedido liberado pelo comercial com medidas e gravações cadastradas.
2. **Impressão 3D:** Operador imprime as ceras e faz a cura.
3. **Pré-Cravação:** Cravador insere as pedras nas garras da cera.
4. **Fundição:** Montagem da árvore de cera, gesso, queima e injeção do metal fundido.
5. **Acabamento / Rolo:** Desgalhamento, usinagem inicial e centrifugação no tambor abrasivo.
6. **Solda / Montagem:** No caso de escapulários e elos de corrente.
7. **Polimento Manual:** Lustro final com escovas e pasta de alto brilho.
8. **Controle de Qualidade (QA):** Lupa, conferência de aro e gravação.
9. **Banho:** Eletrodeposição de ouro 18k, ródio ou verniz antialérgico.
10. **Expedição:** Montagem da caixa, bombom, certificado e despacho.
            `,
            pop: null,
            checklist: [
              "Compreende a sequência de todos os 10 estágios do pedido?",
              "Sabe conferir o operador responsável antes de passar a peça para frente?",
              "Entende como o registro no sistema impacta o cálculo de pontuação da equipe?"
            ]
          }
        ]
      }
    ]
  }
};
